import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useLingui, Trans } from '@lingui/react/macro'
import { getBrandConfig } from '@/config/config'
import { ButtonFilled } from '@vgw/multibrand-fe-button'
import { Link } from '@vgw/multibrand-fe-link'
import { VerificationCell } from '@/features/account-verification/verification-cell/verification-cell'
import { DocumentCell } from '@/features/account-verification/document-cell/document-cell'
import ShieldIconSrcSet from '@/features/raster-assets/shield_300w.png?w=36;72;144&as=srcset&imagetools'
import AccountBankSrcSet from '@/features/raster-assets/account_bank_300w.png?w=36;72;144&as=srcset&imagetools'
import { VerificationDocumentType } from '@/features/account-verification/verification-document-type'
import { IdentityRequiredDocuments } from '@/features/account-verification/identity-required-documents/identity-required-documents'
import { ProofOfBank } from '@/features/account-verification/proof-of-bank/proof-of-bank'
import { useVerificationStatusQuery } from '@/features/account-verification/use-verification-status-query'
import { useMutation } from '@tanstack/react-query'
import { initiateCddSubmissionMutation } from '@/apis/pam-kyc-api'
import { makeCddSubmissionRequest } from '@/features/account-verification/make-cdd-submission-request'
import { useToast } from '@vgw/multibrand-fe-toast'
import { AccountVerificationLayout } from '@/routes/_app/account-verification/-layout'
import { CircleLoadingAnimation } from '@vgw/multibrand-fe-circle-loading-animation'
import { ApiFetchError } from '@/features/api-fetch-error'

export const Route = createFileRoute('/_app/account-verification')({
  component: RouteComponent,
})

function RouteComponent() {
  const { t } = useLingui()

  const navigate = useNavigate()
  const toast = useToast()

  const { data, isPending, error, refetch } = useVerificationStatusQuery()
  const { pamLegalDocumentsUrl } = getBrandConfig()

  const { mutate, isPending: pendingSubmissionInitialization } = useMutation({
    ...initiateCddSubmissionMutation(),
  })

  const startVerificationFlow = (docType?: VerificationDocumentType) => {
    mutate(
      {
        body: makeCddSubmissionRequest(data!, docType),
      },
      {
        onSuccess: (resp) => {
          void navigate({
            to: '/account-verification/tnc',
            search: {
              url: resp.documentValidationRedirectUrl,
            },
          })
        },
        onError: (resp) => {
          toast.show({
            description:
              resp?.detail || t`Something went wrong. Please try again.`,
            variant: 'warning',
            close: false,
          })
        },
      },
    )
  }

  if (error) {
    return <ApiFetchError onRetry={refetch} />
  }

  if (isPending) {
    return (
      <main className="mt-16 flex justify-center">
        <CircleLoadingAnimation />
      </main>
    )
  }

  return (
    <AccountVerificationLayout
      backButtonOnClick={() => {
        void navigate({ to: '/' })
      }}
    >
      <VerificationCell
        icon={<img srcSet={ShieldIconSrcSet} width={36} height={36} alt="" />}
        heading={t`Verify your identity`}
        description={t`Submit a valid ID and proof of address to complete identity verification.`}
        areAllVerified={data.identity.areAllVerified}
        infoDrawerContent={<IdentityRequiredDocuments />}
      >
        <DocumentCell
          documentName={t`Photo ID`}
          documentType={VerificationDocumentType.PHOTO_ID}
          submitButtonLabel={t`Submit`}
          areAllVerified={data.identity.areAllVerified}
          status={data.identity.docs.photoId.status}
          rejectedReasons={data.identity.docs.photoId.rejectedReasons}
          documentIndex={1}
          isIndividualMode={data.isIndividualMode}
          pendingDescription={data.identity.docs.photoId.pendingMessage!}
          verifiedAt={data.identity.docs.photoId.verifiedAt}
          isActionPending={pendingSubmissionInitialization}
          onAction={() =>
            startVerificationFlow(VerificationDocumentType.PHOTO_ID)
          }
        />
        <DocumentCell
          documentName={t`Proof of address`}
          documentType={VerificationDocumentType.ADDRESS}
          submitButtonLabel={t`Submit`}
          areAllVerified={data.identity.areAllVerified}
          status={data.identity.docs.address.status}
          rejectedReasons={data.identity.docs.address.rejectedReasons}
          documentIndex={2}
          isIndividualMode={data.isIndividualMode}
          pendingDescription={data.identity.docs.address.pendingMessage!}
          verifiedAt={data.identity.docs.address.verifiedAt}
          isActionPending={pendingSubmissionInitialization}
          onAction={() =>
            startVerificationFlow(VerificationDocumentType.ADDRESS)
          }
        />
      </VerificationCell>
      <VerificationCell
        optional={true}
        icon={<img srcSet={AccountBankSrcSet} width={36} height={36} alt="" />}
        heading={t`Link Bank Account`}
        description={t`Link a bank account to redeem your Sweeps Coins. Photo ID and Address must be completed for approval.`}
        areAllVerified={data.bankAccount.areAllVerified}
        infoDrawerContent={<ProofOfBank />}
      >
        <DocumentCell
          documentName={t`Bank account`}
          documentType={VerificationDocumentType.BANK_ACCOUNT}
          status={data.bankAccount.docs.bankAccount.status}
          rejectedReasons={data.bankAccount.docs.bankAccount.rejectedReasons}
          documentIndex={3}
          areAllVerified={data.bankAccount.areAllVerified}
          verifiedAt={data.bankAccount.docs.bankAccount.verifiedAt}
          isIndividualMode={data.isIndividualMode}
          submitButtonLabel={t`Link`}
          pendingDescription={data.bankAccount.docs.bankAccount.pendingMessage!}
          isActionPending={pendingSubmissionInitialization}
          onAction={() =>
            startVerificationFlow(VerificationDocumentType.BANK_ACCOUNT)
          }
        />
      </VerificationCell>

      {!data.isIndividualMode && (
        <ButtonFilled
          isLoading={pendingSubmissionInitialization}
          className="mx-auto w-full lg:max-w-md"
          onClick={() => startVerificationFlow()}
        >
          <Trans>Start Verification</Trans>
        </ButtonFilled>
      )}

      <p className="text-md mt-4 text-center font-family-display text-content-tertiary">
        <Trans>
          Our{' '}
          <Link weight="medium" href={`${pamLegalDocumentsUrl}/privacy-policy`}>
            Privacy Policy
          </Link>{' '}
          outlines how we manage personal information.
        </Trans>
      </p>
    </AccountVerificationLayout>
  )
}
